import { connectiveRegex, operatorRegex } from "building-types/conditions/conditionTypes";
import type {
  Condition,
  ConditionGroup,
  ConnectiveOperator,
  ConditionOperator,
} from "building-types/conditions/conditionTypes";
import type { DocEntityFieldTypeDef } from "building-types/conditions/docEntityFieldTypeDefTypes";
import type { Label } from "building-types/entities/labelTypes";
import type { Formula } from "building-types/options/formula";
import type { DocOptionSet } from "building-types/options/optionSet";
import type { Parameter, ParameterValue } from "building-types/options/parameter";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import chunkArray from "common/utils/chunkArray";
import { LRUObjectCache } from "common/utils/lruObjectCache";
import uid from "common/utils/uid";
import { optionSetDefaultOptionId } from "doc/options/optionSetBaseUtils";
import { booleanOptionSetNew } from "doc/options/optionSetTools";
import { entityFieldDefaultOperator, entityFieldDefaultValue } from "../utils/entityFieldTools";
import { formulaNew } from "./formulaNew";
import { isFormula } from "./formulaParsing";
import { DEFAULT_IDENTIFIER_PREFIX, formulaInvert } from "./formulaUtils";

// Compute the combined result of two formulas with an OR operator. The formulas are normally an inverted form.
// So this returns !A or !B = !(A and B)
function formulasOR(formulaA: Formula, formulaB: Formula) {
  if (formulaA.formula === formulaB.formula) {
    return formulaA.formula;
  }
  const invertedA = formulaInvert(formulaA);
  const invertedB = formulaInvert(formulaB);
  const { conditions: conditionsA, conditionGroups: conditionGroupsA } = parseFormulaIntoConditions(invertedA.formula);
  const { conditions: conditionsB, conditionGroups: conditionGroupsB } = parseFormulaIntoConditions(invertedB.formula);
  const hasOR = (condition) => condition.connective === "or";
  const addParentheses =
    conditionsA?.some(hasOR) ||
    conditionsB?.some(hasOR) ||
    (conditionGroupsA && Object.values(conditionGroupsA).some(hasOR)) ||
    (conditionGroupsB && Object.values(conditionGroupsB).some(hasOR));
  if (addParentheses) {
    return `not ((${invertedA.formula}) and (${invertedB.formula}))`;
  }
  return `not (${invertedA.formula} and ${invertedB.formula})`;
}

// Compute the combined result of two formulas with an AND operator. The formulas are normally an inverted form.
// So this returns !A and !B = !(A or B)
function formulasAND(formulaA: Formula, formulaB: Formula) {
  if (formulaA.formula === formulaB.formula) {
    return formulaA.formula;
  }
  const invertedA = formulaInvert(formulaA);
  const invertedB = formulaInvert(formulaB);
  const { conditions: conditionsA, conditionGroups: conditionGroupsA } = parseFormulaIntoConditions(invertedA.formula);
  const { conditions: conditionsB, conditionGroups: conditionGroupsB } = parseFormulaIntoConditions(invertedB.formula);
  const hasAND = (condition) => condition.connective === "and";
  const addParentheses =
    conditionsA?.some(hasAND) ||
    conditionsB?.some(hasAND) ||
    (conditionGroupsA && Object.values(conditionGroupsA).some(hasAND)) ||
    (conditionGroupsB && Object.values(conditionGroupsB).some(hasAND));
  if (addParentheses) {
    return `not ((${invertedA.formula}) or (${invertedB.formula}))`;
  }
  return `not (${invertedA.formula} or ${invertedB.formula})`;
}

export function disabledOR(disabledA?: boolean | Formula, disabledB?: boolean | Formula) {
  if (isFormula(disabledA) && isFormula(disabledB)) {
    return formulaNew(formulasOR(disabledA, disabledB));
  }
  if (disabledA === true || disabledB === true) {
    return true;
  }
  if (!disabledA) {
    return disabledB;
  }
  if (!disabledB) {
    return disabledA;
  }
  return false;
}

export function disabledAND(disabledA?: boolean | Formula, disabledB?: boolean | Formula) {
  if (isFormula(disabledA) && isFormula(disabledB)) {
    return formulaNew(formulasAND(disabledA, disabledB));
  }
  if (!disabledA || !disabledB) {
    return false;
  }
  if (disabledA === true) {
    return disabledB;
  }
  if (disabledB === true) {
    return disabledA;
  }
  return false;
}

export function disabledANDs(disabled: (boolean | Formula | undefined)[]) {
  if (disabled.length === 0) {
    return false;
  }
  if (disabled.length === 1) {
    return disabled[0];
  }
  return disabled.reduce(disabledAND);
}

export type ParseResult =
  | {
      conditions: Condition[];
      conditionGroups: Table<ConditionGroup>;
      error: null;
    }
  | {
      conditions: null;
      conditionGroups: null;
      error: Error;
    };

// A condition can be based on an option set, parameter, entity field, or a label
export type ConditionTypeDef = DocOptionSet | Parameter | DocEntityFieldTypeDef | Label;

export interface InitConditionParams {
  typeDef: ConditionTypeDef | null;
  connective?: ConnectiveOperator;
  groupId?: string;
  optionSets: Table<DocOptionSet>;
}

export function initCondition({ connective, typeDef, groupId, optionSets }: InitConditionParams): Condition {
  let defaultValue: ParameterValue | null | undefined = "";
  let defaultOperator: ConditionOperator = "==";

  if (typeDef) {
    switch (typeDef.entityType) {
      case "OPTION_SET":
        defaultValue = optionSetDefaultOptionId(typeDef, optionSets) ?? "";
        break;
      case "LOCAL_PARAMETER":
      case "GLOBAL_PARAMETER":
      case "SYSTEM_PARAMETER":
        defaultValue = typeDef.defaultValue ?? "";
        break;
      case "ENTITY_FIELD":
        defaultOperator = entityFieldDefaultOperator(typeDef.id);
        defaultValue = entityFieldDefaultValue(typeDef.id);
        break;
      case "LABEL":
        defaultValue = "true";
        break;
      default:
        assertUnreachable(typeDef);
    }
  }

  return {
    typeDefId: typeDef?.id ?? "",
    value: defaultValue,
    operator: defaultOperator,
    connective,
    groupId,
  };
}

const defaultParseResult: ParseResult = {
  conditions: [],
  conditionGroups: {},
  error: null,
};

/**
 *
  @param formula - String representing conditional logic associated with enabling or disabling a given entity,
    i.e. 'not ((KITCHEN == "1" or KITCHEN == "2") and (GARAGE == "Rear Garage" and FOO != "bar") or (BDRM == "1" and BONUS != true))'
  @param idSource - determines the id set for the resulting Condition or Condition Group; used primarily for unit testing
  @returns a ParseResult containing any Conditions or ConditionGroups as well as an error if it exists
 */

export function parseFormulaIntoConditions(formula: string, idSource = () => uid()): ParseResult {
  if (formula === String(false) || formula === String(true)) {
    return defaultParseResult;
  }

  let parseResult = defaultParseResult;

  try {
    const conditions: Condition[] = [];
    const conditionGroups: RWTable<ConditionGroup> = {};

    const connectives: ConnectiveOperator[] = [];
    const segments = segmentFormulaStatementsByConnective(formula);
    const tokens: string[] = [];

    for (const segment of segments) {
      if (["and", "or"].includes(segment)) {
        connectives.push(segment as ConnectiveOperator);
      } else {
        segment.split(operatorRegex).forEach((token) => {
          tokens.push(token);
        });
      }
    }

    // group statements into chunks of three, i.e. [ 'elevationStyle', '==', '"Southern"' ]
    const statements = chunkArray<string>(tokens, 3);

    let withinConditionGroup = false;
    for (let i = 0; i < statements.length; i++) {
      const statement = statements[i];
      // beginning of condition group
      if (statement[0].startsWith("(")) {
        withinConditionGroup = true;
        const groupId = idSource();
        const { typeDefId, operator, value } = parseStatement(statement);

        const groupConditions: Condition[] = [
          {
            groupId,
            typeDefId,
            operator,
            value,
          },
        ];

        // search for end of condition group and a prefacing connective
        for (let j = i + 1; j < statements.length; j++) {
          const nextStatement = statements[j];
          const { typeDefId, operator, value } = parseStatement(nextStatement);

          /**
           * break loop when condition group has single condition, i.e. '(KITCHEN == "1")'
           * this occurs when a condition group has all but one of its conditions removed
           */
          if (statement[2].endsWith(")")) {
            break;
          }

          groupConditions.push({
            groupId,
            typeDefId,
            operator,
            value,
            connective: connectives[j - 1],
          });

          if (nextStatement[2].endsWith(")")) {
            // break loop when condition group is closed
            break;
          }
        }

        conditionGroups[groupId] = {
          id: groupId,
          conditions: groupConditions,
          connective: connectives[i - 1] ?? "and",
        };
      } else if (!withinConditionGroup) {
        const { typeDefId, operator, value } = parseStatement(statement);

        conditions.push({
          connective: connectives[i - 1] as ConnectiveOperator,
          typeDefId,
          operator,
          value,
        });
      }
    }

    parseResult = {
      error: null,
      conditions,
      conditionGroups,
    };
  } catch (error) {
    return {
      conditions: null,
      conditionGroups: null,
      error,
    };
  }

  return parseResult;
}

function extractOptionValue(value: string): string {
  const re = /"(.*?)"/g;
  const result: string[] = [];

  let current: RegExpExecArray | null;

  while ((current = re.exec(value))) {
    assertIsNotNullOrUndefined(current);
    const val = current.pop();
    assertIsNotNullOrUndefined(val);
    result.push(val);
  }

  return result[0];
}

function parseConditionValue(value: string): boolean | string | undefined {
  // meant to handle parsing values at the beginning or end of condition groups, i.e. '("bar"' or '"2")'
  let tempValue = value.replace(/\(|\)/g, "");
  if (["true", "false"].includes(tempValue)) {
    return tempValue === "true";
  }

  tempValue = tempValue.trim();

  // "undefined" is not valid JSON so has to be handled separately
  if (tempValue === "undefined") {
    return undefined;
  }

  // if value is escaped or not a string it must be parsed
  return /\\/g.test(tempValue) || !tempValue.startsWith('"') ? JSON.parse(tempValue) : extractOptionValue(value);
}

export function defaultConditionGroup(): ConditionGroup {
  const groupId = uid();

  const defaultLHSCondition = initCondition({
    typeDef: null,
    groupId,
    connective: undefined,
    optionSets: {}, // OK to pass {} because it's an empty condition anyway
  });

  const defaultRHSCondition = initCondition({
    typeDef: null,
    groupId,
    connective: "and",
    optionSets: {}, // OK to pass {} because it's an empty condition anyway
  });

  return {
    id: groupId,
    conditions: [defaultLHSCondition, defaultRHSCondition],
    connective: "and",
  };
}

export interface CompileFormulaOptions {
  conditions: Condition[];
  conditionGroups: Table<ConditionGroup>;
  typeDefs: Table<ConditionTypeDef>;
  identifierPrefix?: string;
}

/**
 * Takes in a parsed set of Conditions & Condition Groups & evaluates them to be either true or false
 *
 * @param conditions - an array of isolated conditions
 * @param conditionGroups - a table of condition groups
 * @param params - a table of options stored on the doc
 * @param identifierPrefix - a string to prefix all identifiers with
 */
export function compileFormula({
  conditions,
  conditionGroups,
  typeDefs,
  identifierPrefix = "",
}: CompileFormulaOptions): string {
  let formula = "";
  const conditionGroupCount = Object.keys(conditionGroups).length;
  const defaultOptionSet = booleanOptionSetNew();

  // Handle isolated conditions
  for (const condition of conditions) {
    const optionSet = condition.typeDefId ? typeDefs[condition.typeDefId] : defaultOptionSet;

    if (!optionSet) {
      console.warn(`Option Set not found for optionSetId "${condition.typeDefId}"`);
      continue;
    }

    formula += compileCondition(condition, identifierPrefix);
  }

  if (!conditionGroupCount) {
    return formula;
  }

  for (const groupId in conditionGroups) {
    const conditionGroup = conditionGroups[groupId];
    let formulaGroup = "";

    for (const condition of conditionGroup.conditions) {
      const optionSet = condition.typeDefId ? typeDefs[condition.typeDefId] : defaultOptionSet;

      if (!optionSet) {
        console.warn(`Option Set not found for optionSetId "${condition.typeDefId}"`);
        continue;
      }

      formulaGroup += compileCondition(condition, identifierPrefix);
    }

    const connective = `${conditionGroup.connective ? `${conditionGroup.connective} ` : ""}`;
    // Remove parentheses if not necessary, or not part of a group
    formulaGroup = `${connective}${conditions.length || conditionGroupCount > 0 ? `(${formulaGroup})` : formulaGroup}`;
    formula += conditionGroup.connective ? ` ${formulaGroup}` : formulaGroup;
  }

  // If no isolated conditions, remove prefacing connective
  if (!conditions.length) {
    formula = formula.replace(/^\sand|or\s/, "").trim();
  }

  return formula;
}

/**
 * Extracts any option handle referenced by a formula
 *
 * @param formula - a formula
 *
 * @returns a list of option handles
 */

/** Cap unique formula strings retained for option-handle extraction. */
const FORMULA_OPTION_HANDLES_CACHE_SIZE = 256;

const formulaOptionHandlesCache = new LRUObjectCache<string, { [optionSetId: string]: string[] }>(
  FORMULA_OPTION_HANDLES_CACHE_SIZE,
);

export function formulaOptionHandles(formula: string): { [optionSetId: string]: string[] } {
  if (!formula) {
    return {};
  }

  const cached = formulaOptionHandlesCache.get(formula);
  if (cached) {
    return cached;
  }

  const optionRefs: { [optionSetId: string]: string[] } = {};
  const { conditions, conditionGroups } = parseFormulaIntoConditions(formula);

  // flatten all conditions
  const allConditions = Object.values(conditionGroups ?? {}).reduce(
    (acc, group) => acc.concat(group.conditions),
    conditions ?? [],
  );

  for (const condition of Object.values(allConditions)) {
    const optionSetId = sanitizeConditionOptionSetId(condition);
    optionRefs[optionSetId] = [...(optionRefs[optionSetId] ?? []), condition.value as string];
  }

  formulaOptionHandlesCache.add(formula, optionRefs);
  return optionRefs;
}

// It looks like there's an issue in parseFormulaIntoConditions when parsing conditions
// with `not `, so we just special case that here.
export function sanitizeConditionOptionSetId(condition: Condition): string {
  return condition.typeDefId.startsWith("not ") ? condition.typeDefId.slice(4) : condition.typeDefId;
}

export function compileTypeDefIdToIdentifier(id: string, prefix = DEFAULT_IDENTIFIER_PREFIX) {
  return prefix + id;
}

function compileCondition(condition: Condition, prefix?: string) {
  const connective = condition.connective ? ` ${condition.connective} ` : "";
  const value = JSON.stringify(condition.value);

  return `${connective}${compileTypeDefIdToIdentifier(condition.typeDefId, prefix)} ${condition.operator} ${value}`;
}

function removeParens(str: string) {
  return str.replace(/[()]/g, "");
}

export function removeIdentifierPrefix(str: string, identifierPrefix = DEFAULT_IDENTIFIER_PREFIX) {
  return str.startsWith(identifierPrefix) ? str.replace(identifierPrefix, "") : str;
}

function parseStatement(
  statement: string[],
  identifierPrefix?: string,
): Pick<Condition, "typeDefId" | "operator" | "value"> {
  const [typeDefId, operator, value] = statement;

  return {
    typeDefId: removeIdentifierPrefix(removeParens(typeDefId), identifierPrefix),
    operator: operator as ConditionOperator,
    value: parseConditionValue(value),
  };
}

function segmentFormulaStatementsByConnective(str: string) {
  return str
    .split(connectiveRegex)
    .filter(Boolean)
    .map((token) => token.trim());
}
